// based imports and stores
import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth.js';
import { useApiCredentialsStore } from '@/stores/apiCredentials';

// imports axios
import axios from 'axios';

class ApiService {

  constructor(baseURL) {

    this.api = axios.create({
      baseURL: baseURL ?? "https://email-service.momap.it/",
      timeout: 25000
    });

    // Interceptor per aggiungere il token JWT e le credenziali API
    this.api.interceptors.request.use(config => {
      const token = computed(() => useAuthStore().getToken)?.value;
      const apiKey = computed(() => useApiCredentialsStore().getAuthApiKey)?.value;
      const apiSecret = computed(() => useApiCredentialsStore().getAuthApiSecret)?.value;


      // Aggiungiamo le credenziali API
      if (apiKey && apiSecret) {
        config.headers['x-api-key'] = apiKey;
        config.headers['x-api-secret'] = apiSecret;
      }

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    }, error => {
      return Promise.reject(error);
    });

  }

  handleResponse(response) {
    
    if (response?.status !== 200) {
      throw new Error('La chiamata API non è riuscita o non ha successo');
    }

    if(!('data' in response) || response?.data === null) {
      throw new Error('La chiamata API contiene nessun dato');
    }

    if(!('success' in response?.data) || response?.data?.success !== true) {
      throw new Error('La chiamata API ha restituito esito non valido');
    }

    return response?.data;
  }

  handleError(error) {
    
    // Se abbiamo una risposta con success: true, non è un errore
    if (error.response?.data?.success === true) {
      return error.response.data;
    }

    // Se abbiamo una risposta con dati ma senza success, potrebbe essere valida
    if (error.response?.data?.data) {
      return error.response.data;
    }

    throw new Error(error.response?.data?.message || 'Errore nella richiesta');
  }

  async request(method, endpoint, data = null, config = {}) {
    try {
      let response;
      if (method === 'get' || method === 'delete') {
        const axiosConfig = data && typeof data === 'object' && !Array.isArray(data) ? data : config;
        response = await this.api[method](endpoint, axiosConfig);
      } else {
        response = await this.api[method](endpoint, data, config);
      }
      

      // Se la risposta ha success: true, è valida
      if (response.data?.success === true) {
        return response.data;
      }

      // Se la risposta ha dei dati ma non ha success, potrebbe essere valida
      if (response.data?.data) {
        return response.data;
      }

      throw new Error('La risposta non ha una struttura valida');
    } catch (error) {
      if (error.response) {
        return this.handleError(error);
      }
      throw error;
    }
  }

  async get(endpoint, config = {}) {
    return this.request('get', endpoint, config);
  }

  async post(endpoint, data, config = {}) {
    return this.request('post', endpoint, data, config);
  }

  async put(endpoint, data) {
    return this.request('put', endpoint, data);
  }

  async delete(endpoint) {
    return this.request('delete', endpoint);
  }

  async postFile(endpoint, data, options = {}) {
    // Non impostare Content-Type: axios aggiunge automaticamente il boundary multipart
    const mergedOptions = {
      ...options,
      headers: {
        ...(options.headers || {})
      }
    };

    return this.request('post', endpoint, data, mergedOptions);
  }

  setBaseURL(newBaseURL) {
    this.api.defaults.baseURL = newBaseURL;
  }

  setHeaders(headers) {
    this.api.defaults.headers = { ...this.api.defaults.headers, ...headers };
  }
  
}

const HttpService = new ApiService(import.meta.env.VITE_API_URL);

export default HttpService;
